import { useState } from 'react';
import { X } from 'lucide-react';
import { addNegativeKeywordsFromText } from '@/utils/spAdGroupCreate';
import type { SpAddedNegativeKeyword, SpNegativeMatchType } from '@/types/spCampaignCreate';
import { SpAdGroupSection } from '@/components/campaign-create/create-adgroup/SpAdGroupSection';

export function NegativeKeywordTargetingSection({
  negativeKeywords,
  onChange,
  disabled,
}: {
  negativeKeywords: SpAddedNegativeKeyword[];
  onChange: (rows: SpAddedNegativeKeyword[]) => void;
  disabled?: boolean;
}) {
  const [keywordText, setKeywordText] = useState('');
  const [matchType, setMatchType] = useState<SpNegativeMatchType>('NEGATIVE_EXACT');

  const addKeywords = () => {
    if (!keywordText.trim() || disabled) return;
    const result = addNegativeKeywordsFromText(keywordText, matchType, negativeKeywords);
    onChange(result.keywords);
    setKeywordText('');
  };

  return (
    <SpAdGroupSection title="Negative keyword targeting" optional collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border rounded-xl overflow-hidden">
        <div className="p-4 space-y-3 bg-surface">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-foreground">Match type</span>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="negative-keyword-match"
                checked={matchType === 'NEGATIVE_EXACT'}
                onChange={() => setMatchType('NEGATIVE_EXACT')}
                disabled={disabled}
              />
              Negative exact
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="negative-keyword-match"
                checked={matchType === 'NEGATIVE_PHRASE'}
                onChange={() => setMatchType('NEGATIVE_PHRASE')}
                disabled={disabled}
              />
              Negative phrase
            </label>
          </div>
          <textarea
            value={keywordText}
            onChange={(e) => setKeywordText(e.target.value)}
            placeholder="Enter keywords separated by new line."
            className="w-full min-h-[140px] border border-border rounded-lg px-3 py-2 text-sm"
            disabled={disabled}
            aria-label="Negative keywords"
          />
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground disabled:opacity-40"
            disabled={disabled || !keywordText.trim()}
            onClick={addKeywords}
          >
            Add keywords
          </button>
        </div>
        <div className="p-4 bg-page/60 border-t lg:border-t-0 lg:border-l border-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{negativeKeywords.length} added</p>
            {negativeKeywords.length > 0 ? (
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled}
                onClick={() => onChange([])}
              >
                Remove all
              </button>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="py-1 pr-3">Keyword</th>
                  <th className="py-1 pr-3">Match type</th>
                  <th className="py-1 w-8" />
                </tr>
              </thead>
              <tbody>
                {negativeKeywords.map((row) => (
                  <tr key={`${row.keywordText}::${row.matchType}`} className="border-t border-border">
                    <td className="py-2 pr-3 truncate max-w-[12rem]">{row.keywordText}</td>
                    <td className="py-2 pr-3 text-foreground-muted">
                      {row.matchType === 'NEGATIVE_EXACT' ? 'Negative exact' : 'Negative phrase'}
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          onChange(
                            negativeKeywords.filter(
                              (item) =>
                                !(item.keywordText === row.keywordText && item.matchType === row.matchType)
                            )
                          )
                        }
                        aria-label="Remove negative keyword"
                      >
                        <X className="w-4 h-4 text-foreground-muted" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SpAdGroupSection>
  );
}
